import * as React from 'react';
import { CSSTransition } from 'react-transition-group';
import { EnterHandler, ExitHandler, TransitionStatus } from 'react-transition-group/Transition';
import { CSSProp } from 'styled-components';
import { css } from 'twin.macro';
import 'styled-components/macro';

import { handleTransitionEnd } from './handleTransitionEnd';

export type TailwindTransitionProps = {
  appear?: boolean;
  children?: TailwindTransitionChildren;
  enter?: boolean;
  exit?: boolean;
  in?: boolean;
  mountOnEnter?: boolean;
  onEnter?: EnterHandler<undefined>;
  onEntered?: EnterHandler<undefined>;
  onEntering?: EnterHandler<undefined>;
  onExit?: ExitHandler<undefined>;
  onExited?: ExitHandler<undefined>;
  onExiting?: ExitHandler<undefined>;
  styles?: TailwindTransitionStyles;
  unmountOnExit?: boolean;
};

export type TailwindTransitionChildren =
  | React.ReactNode
  | ((status: TransitionStatus, style?: CSSProp) => React.ReactNode);

export interface TailwindTransitionStyles {
  appear?: CSSProp;
  appearActive?: CSSProp;
  appearDone?: CSSProp;
  enter?: CSSProp;
  enterActive?: CSSProp;
  enterDone?: CSSProp;
  exit?: CSSProp;
  exitActive?: CSSProp;
  exitDone?: CSSProp;
}

/**
 * This component wraps the `<CSSTransition />` component from
 * `react-transition-group` to make it more Tailwind-friendly.
 */
export const TailwindTransition = ({ children, styles, ...forwardedProps }: TailwindTransitionProps) => (
  <CSSTransition addEndListener={handleTransitionEnd} {...forwardedProps}>
    {typeof children === 'function' ? status => children(status, styles && getStyle(styles)) : children}
  </CSSTransition>
);

const getStyle = (styles: TailwindTransitionStyles) => css`
  &.appear {
    ${styles.appear}
  }

  &.appear-active {
    ${styles.appearActive}
  }

  &.appear-done {
    ${styles.appearDone}
  }

  &.enter {
    ${styles.enter}
  }

  &.enter-active {
    ${styles.enterActive}
  }

  &.enter-done {
    ${styles.enterDone}
  }

  &.exit {
    ${styles.exit}
  }

  &.exit-active {
    ${styles.exitActive}
  }

  &.exit-done {
    ${styles.exitDone}
  }
`;
